import React from 'react';
import { useClaimRequest, useTelcoPaymentMethods } from '../hooks/usePayment';
import { usePensionsSummary } from '../hooks/dashboard/usePensionSummary';
import { usePolicySummary } from '../hooks/dashboard/usePolicySummary';
import { notifyUser, normalizeGhanaPhone, validateGhanaPhone } from '../util/util';
import { useModalWidth } from '../hooks/useModalWidth';

const toTitleCase = (str) => str ? str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) : '';

const WithdrawalRequestModal = ({ isOpen, onClose, policyNumber, productType }) => {
  const modalWidth = useModalWidth();
  const [withdrawalAmount, setWithdrawalAmount] = React.useState('');
  const [telecomMethod, setTelecomMethod] = React.useState('00465');
  const [momoWallet, setMomoWallet] = React.useState('');
  const [amountError, setAmountError] = React.useState('');
  const [phoneError, setPhoneError] = React.useState('');

  const claimRequestMutation = useClaimRequest();
  const { data: telcoMethodsData, isLoading: isLoadingTelcoMethods } = useTelcoPaymentMethods();
  const { data: pensionsSummaryData } = usePensionsSummary();
  const { data: policySummaryData } = usePolicySummary();
  
  // Determine policy type from prop
  const isPensionPolicy = productType === 'PENSION';
  const isLifePolicy = productType === 'LIFE';
  
  let currentCashValue = 0;
  if (isPensionPolicy) {
    // For pension policies, use total_available_balance from policySummaryData (same as dashboard)
    // Fallback to total_investment from pensionsSummaryData
    currentCashValue = policySummaryData?.data?.total_available_balance || 
                      pensionsSummaryData?.data?.total_investment || 0;
  } else if (isLifePolicy) {
    // For life policies, use total_life_investment from policySummaryData
    currentCashValue = policySummaryData?.data?.total_life_investment || 0;
  } else {
    // Fallback to available balance if type cannot be determined
    currentCashValue = policySummaryData?.data?.total_available_balance || 
                      pensionsSummaryData?.data?.total_investment || 0;
  }
  
  const telcoMethods = telcoMethodsData?.data || [];

  // Handle phone number input with Ghana country code validation
  const handlePhoneChange = (value) => {
    // Normalize the input: handles 05XXXXXXXX → 233XXXXXXXX
    const normalized = normalizeGhanaPhone(value);
    setMomoWallet(normalized);
    
    // Validate only when we have 12 digits
    if (normalized.length === 12) {
      const validation = validateGhanaPhone(normalized);
      setPhoneError(validation.error || '');
    } else if (normalized.length > 0 && normalized.length < 12) {
      setPhoneError('Phone number must be exactly 12 digits (233 + 9 digits)');
    } else {
      setPhoneError('');
    }
  };

  // Validate amount against available balance
  const handleAmountChange = (value) => {
    // Allow only numbers and decimal point
    const sanitizedValue = value.replace(/[^0-9.]/g, '');
    
    // Prevent multiple decimal points
    const parts = sanitizedValue.split('.');
    const cleanValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : sanitizedValue;
    
    const numValue = parseFloat(cleanValue) || 0;
    
    if (numValue > currentCashValue) {
      setAmountError(`Amount cannot exceed available balance of GHS ${currentCashValue.toLocaleString()}`);
    } else if (numValue <= 0 && cleanValue !== '') {
      setAmountError('Amount must be greater than 0');
    } else {
      setAmountError('');
    }
    
    setWithdrawalAmount(cleanValue);
  };

  const omkTextFieldStyles = React.useMemo(() => ({
    width: '100%',
    '--omk-textfield-height': '48px',
    '--omk-textfield-border-radius': '10px',
    '--omk-textfield-padding': '0 16px',
    '--omk-textfield-background': '#fff',
    '--omk-textfield-border-color': '#ddd',
    '--omk-textfield-hover-border-color': '#0D7C4B',
  }), []);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate amount before submission
    const numValue = parseFloat(withdrawalAmount) || 0;
    if (numValue > currentCashValue) {
      notifyUser('error', `Amount cannot exceed available balance of GHS ${currentCashValue.toLocaleString()}`);
      return;
    }
    
    if (numValue <= 0) {
      notifyUser('error', 'Amount must be greater than 0');
      return;
    }
    
    // Validate phone number
    if (!momoWallet || momoWallet.length !== 12) {
      notifyUser('error', 'Please enter a valid 12-digit Ghana mobile number');
      return;
    }
    
    if (phoneError) {
      notifyUser('error', phoneError);
      return;
    }
    
    claimRequestMutation.mutate({
      policyNumber,
      currentCashValue,
      claimAmount: withdrawalAmount,
      telecomMethod,
      momoWallet,
    }, {
      onSuccess: () => {
        onClose();
        notifyUser('success', 'Withdrawal request submitted successfully');
        // Reset form
        setWithdrawalAmount('');
        setAmountError('');
        setPhoneError('');
        setTelecomMethod('00465');
        setMomoWallet('');
      },
      onError: (error) => {
        // Error message is already extracted by extractErrorMessage in usePayment
        notifyUser('error', error?.message || 'Failed to submit withdrawal request. Please try again.');
      },
    });
  };

  if (!isOpen) return null;

  return (
    <div
      className="withdrawal-modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.18)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <style>{`
        @media (max-width: 768px) {
          .withdrawal-modal-overlay {
            align-items: flex-start !important;
            padding-top: 40px !important;
          }
        }
      `}</style>
      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          width: '100%',
          maxWidth: modalWidth,
          maxHeight: '85vh',
          overflowY: 'auto',
          padding: 0,
          boxShadow: '0 16px 60px rgba(0,0,0,0.15)'
        }}
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '24px 32px 20px 32px' }}>
          <div className="modal-header-title" style={{ fontWeight: 800, color: '#222', marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Withdrawal Request</span>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: '#888', cursor: 'pointer', marginLeft: 12, fontWeight: 700, lineHeight: 1 }} aria-label="Close">×</button>
          </div>
          <p className="modal-header-desc" style={{ color: '#888', marginBottom: 0, marginTop: 0 }}>Fill in the details to submit your withdrawal request.</p>
        </div>

        {/* Content */}
        <div style={{ padding: '0 32px 32px 32px' }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div>
            <omk-text-field
              label="Withdrawal Amount"
              name="withdrawal_amount"
              type="text"
              value={withdrawalAmount}
              onInput={(e) => handleAmountChange(e.target.value)}
              required
              style={{
                ...omkTextFieldStyles,
                ...(amountError ? { '--omk-textfield-border-color': '#ff4d4f' } : {})
              }}
              placeholder={`Enter withdrawal amount (max: GHS ${currentCashValue.toLocaleString()})`}
            />
            {amountError && (
              <div className="error-text" style={{ color: '#ff4d4f', marginTop: 4 }}>
                {amountError}
              </div>
            )}
          </div>
          <div>
            <omk-select
              label="Telecom Method"
              name="withdrawal_default_telcomethod"
              value={telecomMethod}
              onInput={(e) => setTelecomMethod(e.target.value)}
              disabled={isLoadingTelcoMethods}
            >
              {isLoadingTelcoMethods ? (
                <omk-select-option value="">Loading...</omk-select-option>
              ) : telcoMethods.length > 0 ? (
                telcoMethods.map((method) => (
                  <omk-select-option key={method.emp_code} value={method.emp_code}>
                    {toTitleCase(method.Name)}
                  </omk-select-option>
                ))
              ) : (
                <omk-select-option value="">No payment methods available</omk-select-option>
              )}
            </omk-select>
          </div>
          <div>
            <omk-text-field
              label="MoMo Wallet Number"
              name="withdrawal_default_momo_wallet"
              type="tel"
              value={momoWallet}
              onInput={(e) => handlePhoneChange(e.target.value)}
              required
              style={{
                ...omkTextFieldStyles,
                ...(phoneError ? { '--omk-textfield-border-color': '#ff4d4f' } : {})
              }}
              placeholder="Enter mobile number (e.g. 05XXXXXXXX or 233XXXXXXXXX)"
              pattern="[0-9]*"
              inputMode="numeric"
              maxLength="12"
            />
            {phoneError && (
              <div className="error-text" style={{ color: '#ff4d4f', marginTop: 4 }}>
                {phoneError}
              </div>
            )}
            {momoWallet && !phoneError && (
              <div className="success-text" style={{ color: '#52c41a', marginTop: 4 }}>
                ✓ Valid Ghana mobile number
              </div>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 4 }}>
            <button type="submit" className="btn btn-primary" disabled={claimRequestMutation.isLoading}>
              {claimRequestMutation.isLoading ? 'Submitting...' : 'Submit'}
            </button>
            <button type="button" className="btn btn-white" onClick={onClose} style={{ padding: '10px 18px', fontWeight: 600 }}>Cancel</button>
          </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default WithdrawalRequestModal;
